/**
 * Web Account & sign-in (core ports AccountSecurity + PasswordSignIn; docs/features/account-sign-in.md):
 * - Settings: /v1/account/* with the access token (emails, password, step-up code, undo), Better Auth /auth/* with the
 *   session cookie (change email, passkeys, two-step setup). Passkeys go through @simplewebauthn/browser.
 * - Sign-in: Better Auth /auth/* (password, second step, reset by code, passkey). The second step's challenge lives
 *   in Better Auth's signed `two_factor` cookie, which the browser keeps between the two calls.
 */
import type { AccountEmail, AccountEmailResponse, ListAccountEmailsResponse, PasswordResponse, UndoEmailChangeResponse } from "@bundle/contracts";
import { AccountSecurityLogic, ApiFailure, type AccountSecurity, type PasswordSignIn, type SessionViewModel } from "@bundle/core";
import { authCall } from "./auth.ts";
import { desktopInfo, isDesktop } from "./desktop.ts";
import { passkeyCeremonyFailure, passkeyFailure, secondFactorCode } from "./passkeyErrors.ts";
import { API_BASE, MOCK } from "./env.ts";
import { v1 } from "./linking.ts";

type PublicKeyJSON = Parameters<typeof import("@simplewebauthn/browser").startRegistration>[0]["optionsJSON"];

/** GET on /auth/* with a query string (authCall's paths carry it). */
const authGet = <T>(path: string) => authCall<T>("GET", path);

/** No-session v1 call (the undo link). */
async function publicPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { method: "POST", headers: { "content-type": "application/json", accept: "application/json" }, body: JSON.stringify(body) }).catch(
    (e: unknown) => {
      throw new ApiFailure("network", 0, "network", e instanceof Error ? e.message : "network error");
    },
  );
  const json = (await res.json().catch(() => undefined)) as { error?: { code?: string; message?: string } } | undefined;
  if (!res.ok) throw new ApiFailure(res.status === 429 ? "rateLimited" : "invalid", res.status, json?.error?.code ?? String(res.status), json?.error?.message ?? res.statusText);
  return json as T;
}

const proofBody = (p: { password?: string; emailCode?: string }) => ({ ...(p.password ? { password: p.password } : {}), ...(p.emailCode ? { emailCode: p.emailCode } : {}) });

export function createAccountSecurity(session: SessionViewModel, base: string = API_BASE): AccountSecurity {
  return {
    emails: async () => (await v1<ListAccountEmailsResponse>(session, "accountEmails", {}, base)).emails,
    addEmail: async (email) => (await v1<AccountEmailResponse>(session, "addAccountEmail", {}, base, { email })).email,
    verifyEmail: async (emailId, code) => (await v1<AccountEmailResponse>(session, "verifyAccountEmail", { emailId }, base, { code })).email,
    async removeEmail(emailId) {
      await v1(session, "removeAccountEmail", { emailId }, base);
    },
    makePrimary: async (emailId) => (await v1<{ emails: AccountEmail[] }>(session, "makePrimaryEmail", { emailId }, base, {})).emails,
    async requestEmailChange(newEmail) {
      await authCall("POST", "/email-otp/request-email-change", { newEmail });
    },
    async confirmEmailChange(newEmail, code) {
      await authCall("POST", "/email-otp/change-email", { newEmail, otp: AccountSecurityLogic.normalizeCode(code) });
    },
    undoEmailChange: async (token) => (await publicPost<UndoEmailChangeResponse>("/v1/account/email-change/undo", { token })).email,
    async sendStepUpCode() {
      await v1(session, "stepUpCode", {}, base, {});
    },
    setPassword: async (req) => (await v1<PasswordResponse>(session, "setPassword", {}, base, req)).signedOut,
    async removePassword(currentPassword) {
      await v1(session, "removePassword", {}, base, { currentPassword });
    },
    async addPasskey(name) {
      const { startRegistration } = await import("@simplewebauthn/browser");
      const q = name ? `?name=${encodeURIComponent(name)}` : "";
      const optionsJSON = await authGet<PublicKeyJSON>(`/passkey/generate-register-options${q}`);
      const response = await startRegistration({ optionsJSON });
      await authCall("POST", "/passkey/verify-registration", { response, ...(name ? { name } : {}) });
    },
    async renamePasskey(id, name) {
      await authCall("POST", "/passkey/update-passkey", { id, name });
    },
    async removePasskey(id) {
      await authCall("POST", "/passkey/delete-passkey", { id });
    },
    async startTwoFactor(proof) {
      const res = await authCall<{ totpURI: string; backupCodes: string[] }>("POST", "/two-factor/enable", proofBody(proof));
      return { totpURI: res.totpURI, secret: AccountSecurityLogic.totpSecretOf(res.totpURI) ?? "", backupCodes: res.backupCodes };
    },
    async confirmTwoFactor(code) {
      await authCall("POST", "/two-factor/verify-totp", { code: AccountSecurityLogic.normalizeCode(code) });
    },
    async disableTwoFactor(proof) {
      await authCall("POST", "/two-factor/disable", proofBody(proof));
    },
    regenerateBackupCodes: async (proof) => (await authCall<{ backupCodes: string[] }>("POST", "/two-factor/generate-backup-codes", proofBody(proof))).backupCodes,
  };
}

export const webPasswordSignIn: PasswordSignIn = {
  signInWithPassword: async (email, password) => AccountSecurityLogic.signInStepOf(await authCall("POST", "/sign-in/email", { email, password })),
  async verifySecondFactor(code, { backup, trustDevice }) {
    const path = backup ? "/two-factor/verify-backup-code" : "/two-factor/verify-totp";
    await authCall("POST", path, { code: secondFactorCode(code, backup), trustDevice });
  },
  async requestPasswordReset(email) {
    await authCall("POST", "/email-otp/request-password-reset", { email });
  },
  async resetPassword(email, code, newPassword, signOutOthers) {
    await authCall("POST", "/email-otp/reset-password", { email, otp: AccountSecurityLogic.normalizeCode(code), password: newPassword, signOutOthers });
    return AccountSecurityLogic.signInStepOf(await authCall("POST", "/sign-in/email", { email, password: newPassword }));
  },
  async signInWithPasskey(opts) {
    const { startAuthentication, browserSupportsWebAuthnAutofill } = await import("@simplewebauthn/browser");
    const autofill = opts?.autofill === true;
    if (autofill && !(await browserSupportsWebAuthnAutofill())) throw passkeyFailure("passkey_aborted");
    const optionsJSON = await authGet<Parameters<typeof startAuthentication>[0]["optionsJSON"]>("/passkey/generate-authenticate-options");
    let response: Awaited<ReturnType<typeof startAuthentication>>;
    try {
      response = await startAuthentication({ optionsJSON, useBrowserAutofill: autofill });
    } catch (e) {
      throw passkeyCeremonyFailure(e);
    }
    try {
      await authCall("POST", "/passkey/verify-authentication", { response });
    } catch (e) {
      // A passkey this server doesn't know (deleted in Settings, or another deployment's).
      if (e instanceof ApiFailure && e.status >= 400 && e.status < 500 && e.status !== 429) throw passkeyFailure("passkey_not_found");
      throw e;
    }
  },
  cancelPasskey() {
    void import("@simplewebauthn/browser").then(({ WebAuthnAbortService }) => WebAuthnAbortService.cancelCeremony());
  },
  passkeysAvailable: () => typeof window !== "undefined" && window.PublicKeyCredential !== undefined,
};


/** Account & sign-in for the app shell: the real API, or the in-browser mock (code-split). */
export function accountSecurityFor(container: { mock: boolean; session: SessionViewModel }): AccountSecurity {
  if (!container.mock && isDesktop()) {
    // The desktop app has no https origin for WebAuthn: adding a passkey happens on the same page in the system browser.
    const real = createAccountSecurity(container.session);
    return {
      ...real,
      async addPasskey() {
        const web = desktopInfo()?.webUrl ?? "https://app.bundle.direct";
        window.open(`${web}${location.pathname}`, "_blank", "noopener");
        throw passkeyFailure("passkey_aborted");
      },
    };
  }
  if (!container.mock) return createAccountSecurity(container.session);
  const session = container.session;
  const ready = import("./mock/accountSecurity.ts").then(({ createMockAccountSecurity }) =>
    createMockAccountSecurity(() => ({ userId: session.state.session?.userId ?? "", email: session.state.session?.email ?? "" })),
  );
  return new Proxy({} as AccountSecurity, {
    get: (_t, key: keyof AccountSecurity) => (...args: unknown[]) => ready.then((s) => (s[key] as (...a: unknown[]) => unknown)(...args)),
  });
}

/** The undo link of a primary-email change (no session): resolves with the restored address. */
export async function undoEmailChange(token: string): Promise<string> {
  if (MOCK) {
    const { createMockAccountSecurity } = await import("./mock/accountSecurity.ts");
    await createMockAccountSecurity(() => ({ userId: "mock-undo", email: "ada@acme.test" })).undoEmailChange(token);
    return "ada@acme.test";
  }
  return (await publicPost<UndoEmailChangeResponse>("/v1/account/email-change/undo", { token })).email;
}
